import React, { useEffect, useState } from "react";
import { domToJpeg } from "modern-screenshot";
import { t } from "../i18n";
import { currentPath, gameIdFromPath } from "../router";
import { useFeatures } from "../features";

const API = process.env.REACT_APP_API_URL || "";

// La capture est prise AVANT d'ouvrir la fenêtre : on veut l'écran tel que le
// joueur l'a vu, pas le formulaire posé dessus. Réduite à 1280 px de large et
// en JPEG, elle tient sous les 4 Mo acceptés par le serveur.
async function captureScreen() {
  const el = document.documentElement;
  const scale = Math.min(1, 1280 / Math.max(1, el.clientWidth)) * Math.min(2, window.devicePixelRatio || 1);
  return domToJpeg(el, {
    quality: 0.7,
    scale,
    width: el.clientWidth,
    height: el.clientHeight,
    style: { transform: `translateY(-${window.scrollY}px)` },
    // Pas le <noscript> que la capture ferait réapparaître.
    filter: node => node.tagName !== "NOSCRIPT",
  });
}

function currentToken() {
  try { return JSON.parse(localStorage.getItem("popuquiz_account"))?.token || null; }
  catch (_) { return null; }
}

// Fenêtre « signaler un bug », ouverte depuis le menu du profil (événement
// BUG_REPORT_EVENT) : un bouton flottant chevauchait le contenu des écrans de
// jeu. Pendant une partie, le serveur complète le signalement (mode,
// question, période, joueur) ; ailleurs, on note la page.
export const BUG_REPORT_EVENT = "pq:bug-report";

export default function BugReport() {
  // La partie et le joueur se lisent dans l'adresse et le stockage local : le
  // bouton vit dans l'en-tête, qui ne connaît ni l'une ni l'autre.
  const gameId = gameIdFromPath(currentPath());
  let playerId = null;
  try { playerId = localStorage.getItem("popuquiz_playerId"); } catch (_) {}
  const [state, setState] = useState("idle"); // idle | capturing | open | sending | sent
  const [shot, setShot] = useState(null);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [sentId, setSentId] = useState(null);
  const [ticket, setTicket] = useState(null);
  const features = useFeatures();

  const open = async () => {
    if (state !== "idle") return;
    setState("capturing");
    // Deux images pour laisser le menu se refermer : la capture doit montrer
    // l'écran, pas le menu.
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
    // Le serveur fige la question et la période à cet instant précis.
    const held = !gameId ? Promise.resolve(null) : fetch(`${API}/reports/ticket`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ gameId }),
    }).then(r => r.json()).then(d => d.ticket || null).catch(() => null);
    let img = null;
    try { img = await captureScreen(); } catch (_) { /* sans capture, le signalement part quand même */ }
    setTicket(await held);
    setShot(img); setTitle(""); setComment(""); setError("");
    setState("open");
  };

  const close = () => { setState("idle"); setShot(null); };

  const send = async (e) => {
    e.preventDefault();
    if (!title.trim() || state === "sending") return;
    setState("sending"); setError("");
    try {
      const token = currentToken();
      const r = await fetch(`${API}/reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ gameId, playerId, ticket, page: currentPath(), title: title.trim(), comment: comment.trim(), screenshot: shot }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || t("bugReport.error"));
      setSentId(d.id);
      setState("sent");
      setTimeout(close, 2200);
    } catch (err) {
      setError(err.message);
      setState("open");
    }
  };

  useEffect(() => {
    window.addEventListener(BUG_REPORT_EVENT, open);
    return () => window.removeEventListener(BUG_REPORT_EVENT, open);
  });

  // Signalement retiré par l'administration : rien à afficher.
  if (!features.bugReport) return null;

  return (
    <div className="bug-anchor">

      {(state === "open" || state === "sending" || state === "sent") && (
        <div className="bug-overlay" onClick={state === "sent" ? close : undefined}>
          <form className="bug-modal" onClick={e => e.stopPropagation()} onSubmit={send}>
            {state === "sent" ? (
              <p className="bug-sent">{t("bugReport.sent", { id: sentId })}</p>
            ) : (
              <>
                <h3>{t("bugReport.title")}</h3>
                {shot
                  ? <img className="bug-shot" src={shot} alt={t("bugReport.screenshot")} />
                  : <p className="bug-noshot">{t("bugReport.noScreenshot")}</p>}
                <input className="bug-input" value={title} maxLength={80} autoFocus
                  placeholder={t("bugReport.titlePlaceholder")} onChange={e => setTitle(e.target.value)} />
                <textarea className="bug-input bug-comment" value={comment} maxLength={2000} rows={4}
                  placeholder={t("bugReport.commentPlaceholder")} onChange={e => setComment(e.target.value)} />
                {error && <p className="bug-error">{error}</p>}
                <div className="bug-actions">
                  <button type="button" className="btn" onClick={close}>{t("bugReport.cancel")}</button>
                  <button type="submit" className="btn btn-primary" disabled={!title.trim() || state === "sending"}>
                    {state === "sending" ? t("bugReport.sending") : t("bugReport.send")}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      )}
    </div>
  );
}
